/* global Office */
import { MSG_SOURCE, REQUEST_TIMEOUT_MS } from "./messageOps";

/**
 * Dialog Bridge
 * Used by the Password Manager dialog to send requests to the sidebar (Crypto Host).
 *
 * Dual transport:
 * - Production (Office Dialog API): Sends via Office.context.ui.messageParent(),
 *   receives via Office.context.ui.addHandlerAsync(DialogParentMessageReceived)
 * - Dev mode (window.open): Sends via window.opener.postMessage(),
 *   receives via window "message" event
 *
 * correlationId is strictly for window-to-window communication – NEVER sent to backend.
 */

/** Pending request map: correlationId -> { resolve, reject, timeoutId } */
const pendingMap = new Map();

/** Whether the response listener has been initialized */
let listenerInitialized = false;

/**
 * Detects the current transport mode.
 * @returns {"office" | "dev"}
 */
function detectTransport() {
  if (
    typeof Office !== "undefined" &&
    Office.context?.ui?.messageParent &&
    window.__DIALOG_MODE__ === true
  ) {
    return "office";
  }
  return "dev";
}

/**
 * Initializes the response listener (called once on first request).
 */
function ensureListener() {
  if (listenerInitialized) return;
  listenerInitialized = true;

  const transport = detectTransport();

  if (transport === "office") {
    // Office Dialog API: listen for messages from parent (sidebar)
    try {
      Office.context.ui.addHandlerAsync(
        Office.EventType.DialogParentMessageReceived,
        (arg) => {
          handleIncomingResponse(arg.message);
        },
      );
      console.log("[DialogBridge] Office DialogParentMessageReceived listener registered");
    } catch (err) {
      console.warn("[DialogBridge] Failed to register Office listener:", err);
      // Fallback: also listen on window for dev/testing
      window.addEventListener("message", handleWindowMessage);
    }
  } else {
    // Dev mode: listen for postMessage from opener (sidebar)
    window.addEventListener("message", handleWindowMessage);
    console.log("[DialogBridge] Dev mode postMessage listener registered");
  }
}

/**
 * Handles window "message" events (dev mode transport).
 */
function handleWindowMessage(event) {
  if (!event.data || event.data.source !== MSG_SOURCE) return;
  if (!event.data.correlationId) return;
  handleIncomingResponse(
    typeof event.data === "string" ? event.data : JSON.stringify(event.data),
  );
}

/**
 * Processes an incoming response string (from either transport).
 */
function handleIncomingResponse(messageString) {
  let parsed;
  try {
    parsed = typeof messageString === "object" ? messageString : JSON.parse(messageString);
  } catch (_) {
    return; // Not JSON
  }

  if (parsed.source !== MSG_SOURCE || !parsed.correlationId) return;

  const pending = pendingMap.get(parsed.correlationId);
  if (!pending) {
    console.warn("[DialogBridge] No pending request for correlationId:", parsed.correlationId);
    return;
  }

  // Cleanup
  clearTimeout(pending.timeoutId);
  pendingMap.delete(parsed.correlationId);

  // Resolve or reject
  if (parsed.success) {
    pending.resolve(parsed.data);
  } else {
    const error = new Error(parsed.data?.error || parsed.errorCode || "Bridge request failed");
    error.errorCode = parsed.errorCode;
    pending.reject(error);
  }
}

/**
 * Sends a request to the sidebar and returns a promise that resolves with the response data.
 *
 * @param {string} op - Operation from OPS (e.g., OPS.GET_STATE)
 * @param {object} payload - Optional payload data
 * @returns {Promise<any>} Response data from the sidebar
 */
export function sendRequest(op, payload = {}) {
  ensureListener();

  return new Promise((resolve, reject) => {
    const correlationId = window.crypto.randomUUID();

    const timeoutId = setTimeout(() => {
      pendingMap.delete(correlationId);
      reject(new Error(`Bridge request timed out after ${REQUEST_TIMEOUT_MS}ms (op: ${op})`));
    }, REQUEST_TIMEOUT_MS);

    pendingMap.set(correlationId, { resolve, reject, timeoutId });

    const message = {
      source: MSG_SOURCE,
      op,
      correlationId,
      payload,
    };

    const transport = detectTransport();

    if (transport === "office") {
      // Office Dialog API: send to parent via messageParent
      try {
        Office.context.ui.messageParent(JSON.stringify(message));
      } catch (err) {
        clearTimeout(timeoutId);
        pendingMap.delete(correlationId);
        reject(new Error("Failed to send message via Office API: " + err.message));
      }
    } else {
      // Dev mode: send to opener via postMessage
      const target = window.opener;
      if (!target) {
        clearTimeout(timeoutId);
        pendingMap.delete(correlationId);
        reject(new Error("No parent window found (window.opener is null)"));
        return;
      }
      target.postMessage(message, "*");
    }
  });
}

/**
 * Cleans up all pending requests (call on dialog unmount).
 */
export function cleanup() {
  for (const [, pending] of pendingMap) {
    clearTimeout(pending.timeoutId);
    pending.reject(new Error("Dialog bridge cleanup – all pending requests cancelled"));
  }
  pendingMap.clear();
}
